import PropTypes from 'prop-types';
import { Box, Typography, Button } from '@mui/material';
import { Iconify } from 'src/components';

/**
 * AISummaryPrompt - Prominent AI call summary prompt
 * Designed to be the primary CTA that draws user attention
 */
const AISummaryPrompt = ({
  onGenerateSummary,
  isLoading = false,
  disabled = false,
}) => (
    <Box
      sx={{
        mb: 2,
        borderRadius: '16px',
        overflow: 'hidden',
        background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 50%, #a855f7 100%)',
        boxShadow: '0 12px 40px rgba(99, 102, 241, 0.35)',
        position: 'relative',
        cursor: disabled ? 'default' : 'pointer',
        transition: 'all 0.3s ease',
        '&:hover': disabled ? {} : {
          transform: 'translateY(-2px)',
          boxShadow: '0 16px 48px rgba(99, 102, 241, 0.45)',
        },
      }}
      onClick={disabled || isLoading ? undefined : onGenerateSummary}
    >
      {/* Animated shimmer effect */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: '-100%',
          width: '100%',
          height: '100%',
          background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent)',
          animation: 'shimmer 3s infinite',
          '@keyframes shimmer': {
            '0%': { left: '-100%' },
            '100%': { left: '100%' },
          },
          pointerEvents: 'none',
        }}
      />

      {/* Decorative elements */}
      <Box
        sx={{
          position: 'absolute',
          top: -30,
          right: -30,
          width: 100,
          height: 100,
          borderRadius: '50%',
          background: 'rgba(255, 255, 255, 0.1)',
          pointerEvents: 'none',
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          bottom: -20,
          left: 20,
          width: 60,
          height: 60,
          borderRadius: '50%',
          background: 'rgba(255, 255, 255, 0.08)',
          pointerEvents: 'none',
        }}
      />

      <Box sx={{ p: 2.5, position: 'relative', zIndex: 1 }}>
        {/* Main content row */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
          }}
        >
          {/* Left side - Icon and text */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1 }}>
            <Box
              sx={{
                width: 52,
                height: 52,
                borderRadius: '14px',
                bgcolor: 'rgba(255, 255, 255, 0.2)',
                backdropFilter: 'blur(10px)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                animation: 'pulse 2s ease-in-out infinite',
                '@keyframes pulse': {
                  '0%, 100%': { transform: 'scale(1)' },
                  '50%': { transform: 'scale(1.05)' },
                },
              }}
            >
              <Iconify icon="mdi:auto-fix" width={28} height={28} sx={{ color: '#fff' }} />
            </Box>
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.25 }}>
                <Typography
                  sx={{
                    fontSize: '1.125rem',
                    fontWeight: 700,
                    color: '#fff',
                    lineHeight: 1.2,
                  }}
                >
                  AI Call Summary
                </Typography>
                <Box
                  sx={{
                    px: 1,
                    py: 0.25,
                    bgcolor: 'rgba(255, 255, 255, 0.25)',
                    borderRadius: '4px',
                    fontSize: '0.625rem',
                    fontWeight: 700,
                    color: '#fff',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                  }}
                >
                  Recommended
                </Box>
              </Box>
              <Typography
                sx={{
                  fontSize: '0.8125rem',
                  color: 'rgba(255, 255, 255, 0.9)',
                  lineHeight: 1.3,
                }}
              >
                Get next steps, concerns, readiness score & more
              </Typography>
            </Box>
          </Box>

          {/* Right side - CTA Button */}
          <Button
            onClick={(e) => {
              e.stopPropagation();
              if (!disabled && !isLoading) onGenerateSummary();
            }}
            disabled={disabled || isLoading}
            sx={{
              py: 1.25,
              px: 2.5,
              bgcolor: '#fff',
              color: '#6366f1',
              fontSize: '0.875rem',
              fontWeight: 700,
              borderRadius: '12px',
              textTransform: 'none',
              boxShadow: '0 4px 16px rgba(0, 0, 0, 0.2)',
              whiteSpace: 'nowrap',
              minWidth: 'auto',
              transition: 'all 0.2s ease',
              display: 'flex',
              alignItems: 'center',
              gap: 0.75,
              '&:hover': {
                bgcolor: '#fff',
                transform: 'scale(1.05)',
                boxShadow: '0 6px 20px rgba(0, 0, 0, 0.25)',
              },
              '&:disabled': {
                bgcolor: 'rgba(255, 255, 255, 0.5)',
                color: 'rgba(99, 102, 241, 0.5)',
              },
            }}
          >
            {isLoading ? (
              <>
                <Iconify
                  icon="mdi:loading"
                  width={20}
                  height={20}
                  sx={{
                    animation: 'spin 1s linear infinite',
                    '@keyframes spin': {
                      '0%': { transform: 'rotate(0deg)' },
                      '100%': { transform: 'rotate(360deg)' },
                    },
                  }}
                />
                Generating...
              </>
            ) : (
              <>
                <Iconify icon="mdi:sparkles" width={20} height={20} />
                Generate
              </>
            )}
          </Button>
        </Box>
      </Box>
    </Box>
  );

AISummaryPrompt.propTypes = {
  onGenerateSummary: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
  disabled: PropTypes.bool,
};

export default AISummaryPrompt;
